import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { NoteFlag } from '@app/features/visits/noteFlag';

import NoteComposer from './NoteComposer';
import NotesTimeline from './NotesTimeline';
import type { NoteFlagValue } from './notes.types';
import { useMarkNotesRead, useUpdateNote, useVisitNotes } from './useNotes';

type VisitNotesSectionProps = {
  visitId: number;
  /** The visit's client — the note is filed under the client, anchored to the visit. */
  clientId: number;
  maxHeight?: number;
  /**
   * Let the reader set each note's flag. Off by default: only a caller that has
   * checked the signed-in user may classify someone else's note turns it on.
   */
  canReclassify?: boolean;
  /** Off where the surrounding chrome already names the section. */
  showHeader?: boolean;
};

/**
 * Every note written on one visit, plus a box to add one.
 *
 * No visit links on the rows: the reader is already on the visit these belong to.
 * The composer posts to the client's endpoint with this visit's id, which is what
 * anchors the note — the composite foreign key makes it impossible to file it under
 * a different client than the visit's.
 */
const VisitNotesSection: React.FC<VisitNotesSectionProps> = ({
  visitId,
  clientId,
  maxHeight = 320,
  canReclassify = false,
  showHeader = true,
}) => {
  const { t } = useTranslation();
  const { notes, areNotesLoading, hasNotesError } = useVisitNotes(visitId);
  const { markRead } = useMarkNotesRead();
  const { updateNote } = useUpdateNote(clientId);
  const [savingFlagNoteId, setSavingFlagNoteId] = useState<number | null>(null);

  const unreadKey = useMemo(
    () =>
      notes
        .filter((note) => !note.isRead)
        .map((note) => note.noteId)
        .join(','),
    [notes]
  );

  useEffect(() => {
    if (unreadKey.length === 0) return;
    markRead(unreadKey.split(',').map(Number));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unreadKey]);

  const handleFlagChange = useCallback(
    async (noteId: number, flag: NoteFlag | null) => {
      setSavingFlagNoteId(noteId);
      try {
        await updateNote({ noteId, payload: { flag: (flag as NoteFlagValue | null) ?? null } });
      } catch {
        // The list is refetched on success only, so a failed change leaves the chip
        // showing the value the server still holds.
      } finally {
        setSavingFlagNoteId(null);
      }
    },
    [updateNote]
  );

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      {showHeader && (
        <div className="border-0 border-b border-slate-100 px-3 py-2 text-[13px] font-semibold text-slate-700">
          {t('notes-visit-panel-title')}
        </div>
      )}

      <NotesTimeline
        notes={notes}
        isLoading={areNotesLoading}
        hasError={hasNotesError}
        emptyKey="notes-visit-empty"
        showVisitLinks={false}
        maxHeight={maxHeight}
        onFlagChange={canReclassify ? (noteId, flag) => void handleFlagChange(noteId, flag) : undefined}
        savingFlagNoteId={savingFlagNoteId}
      />

      {!hasNotesError && <NoteComposer clientId={clientId} visitId={visitId} />}
    </div>
  );
};

export default VisitNotesSection;
